import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Centred modal dialog, async API.
 *
 * Usage (from anywhere):
 *
 *   import { dialog } from "@/components/Dialog";
 *
 *   if (!(await dialog.confirm("Delete this client?"))) return;
 *   await dialog.alert("Upload failed. Check the console.");
 *   const name = await dialog.prompt("Rename drawing:", { initial: "Floor 1" });
 *
 * Mount `<DialogRoot />` once near the top of the tree (we do it in
 * `App.tsx`). Without the root mounted the helpers fall back to the
 * native browser dialog so nothing blocks.
 *
 * No React Context — a tiny module-level pubsub handler is enough. The
 * root component registers itself on mount and unregisters on unmount.
 */

type Variant = "info" | "danger";

interface ConfirmOpts {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: Variant;
}

interface AlertOpts {
  title?: string;
}

interface PromptOpts {
  title?: string;
  initial?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

type Request =
  | ({ kind: "confirm"; message: string; resolve: (ok: boolean) => void } & ConfirmOpts)
  | ({ kind: "alert"; message: string; resolve: () => void } & AlertOpts)
  | ({
      kind: "prompt";
      message: string;
      resolve: (value: string | null) => void;
    } & PromptOpts);

let currentHandler: ((req: Request) => void) | null = null;

function register(fn: (req: Request) => void) {
  currentHandler = fn;
}
function unregister(fn: (req: Request) => void) {
  if (currentHandler === fn) currentHandler = null;
}

export const dialog = {
  confirm(message: string, opts: ConfirmOpts = {}): Promise<boolean> {
    return new Promise((resolve) => {
      if (!currentHandler) {
        // Fallback when the root isn't mounted (tests, non-app routes).
        resolve(window.confirm(message));
        return;
      }
      currentHandler({ kind: "confirm", message, ...opts, resolve });
    });
  },
  alert(message: string, opts: AlertOpts = {}): Promise<void> {
    return new Promise((resolve) => {
      if (!currentHandler) {
        window.alert(message);
        resolve();
        return;
      }
      currentHandler({ kind: "alert", message, ...opts, resolve });
    });
  },
  prompt(message: string, opts: PromptOpts = {}): Promise<string | null> {
    return new Promise((resolve) => {
      if (!currentHandler) {
        resolve(window.prompt(message, opts.initial ?? "") ?? null);
        return;
      }
      currentHandler({ kind: "prompt", message, ...opts, resolve });
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────
// Root component
// ─────────────────────────────────────────────────────────────────────────

export function DialogRoot() {
  const [req, setReq] = useState<Request | null>(null);
  const [promptValue, setPromptValue] = useState("");

  useEffect(() => {
    function handler(next: Request) {
      setReq(next);
      if (next.kind === "prompt") setPromptValue(next.initial ?? "");
    }
    register(handler);
    return () => unregister(handler);
  }, []);

  // Global key bindings while dialog is open.
  useEffect(() => {
    if (!req) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") cancel();
      if (e.key === "Enter" && req && req.kind !== "prompt") confirm();
      // prompt handles Enter itself via the input onKeyDown
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [req]);

  function confirm() {
    if (!req) return;
    if (req.kind === "confirm") req.resolve(true);
    else if (req.kind === "alert") req.resolve();
    else req.resolve(promptValue);
    setReq(null);
  }
  function cancel() {
    if (!req) return;
    if (req.kind === "confirm") req.resolve(false);
    else if (req.kind === "alert") req.resolve();
    else req.resolve(null);
    setReq(null);
  }

  if (!req) return null;

  const variant: Variant = "variant" in req && req.variant ? req.variant : "info";
  const confirmCol =
    variant === "danger" ? "var(--neo-red)" : "var(--neo-teal)";
  const confirmBg =
    variant === "danger" ? "var(--neo-red)" : "var(--neo-teal)";

  const confirmLabel =
    req.kind === "confirm"
      ? (req.confirmLabel ?? (variant === "danger" ? "Delete" : "OK"))
      : req.kind === "prompt"
        ? (req.confirmLabel ?? "Save")
        : "OK";
  const cancelLabel =
    req.kind === "confirm"
      ? (req.cancelLabel ?? "Cancel")
      : req.kind === "prompt"
        ? (req.cancelLabel ?? "Cancel")
        : undefined;
  const title =
    req.kind === "alert"
      ? (req.title ?? "Heads up")
      : req.kind === "confirm"
        ? (req.title ?? (variant === "danger" ? "Confirm delete" : "Confirm"))
        : (req.title ?? "Input needed");

  const overlay = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="neo-dialog-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) cancel();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        backdropFilter: "blur(2px)",
        animation: "neo-dialog-fade .15s ease-out",
      }}
    >
      <div
        style={{
          background: "var(--neo-bg1)",
          border: "1px solid var(--neo-border)",
          borderRadius: 12,
          padding: "22px 24px 18px",
          minWidth: 340,
          maxWidth: 520,
          width: "calc(100% - 48px)",
          boxShadow: "0 20px 50px rgba(0,0,0,.35)",
          animation: "neo-dialog-pop .18s ease-out",
          fontFamily: "'DM Sans', system-ui, sans-serif",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div
          id="neo-dialog-title"
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: "var(--neo-text)",
            marginBottom: 10,
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 13,
            color: "var(--neo-text-muted)",
            lineHeight: 1.5,
            marginBottom: 18,
            whiteSpace: "pre-wrap",
          }}
        >
          {req.message}
        </div>

        {req.kind === "prompt" && (
          <input
            autoFocus
            value={promptValue}
            onChange={(e) => setPromptValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") confirm();
              if (e.key === "Escape") cancel();
            }}
            placeholder={req.placeholder}
            style={{
              width: "100%",
              fontSize: 13,
              padding: "7px 10px",
              borderRadius: 6,
              border: "1px solid var(--neo-border)",
              background: "var(--neo-bg2)",
              color: "var(--neo-text)",
              outline: "none",
              marginBottom: 16,
              fontFamily: "inherit",
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = "var(--neo-teal)";
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = "var(--neo-border)";
            }}
          />
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          {cancelLabel && (
            <button
              onClick={cancel}
              autoFocus={req.kind === "confirm" && variant === "danger"}
              style={{
                padding: "7px 14px",
                borderRadius: 6,
                border: "1px solid var(--neo-border)",
                background: "var(--neo-bg2)",
                color: "var(--neo-text-muted)",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 600,
                fontFamily: "inherit",
              }}
            >
              {cancelLabel}
            </button>
          )}
          <button
            onClick={confirm}
            autoFocus={
              req.kind === "alert" ||
              (req.kind === "confirm" && variant !== "danger")
            }
            style={{
              padding: "7px 16px",
              borderRadius: 6,
              border: "none",
              background: confirmBg,
              color: "#fff",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 700,
              fontFamily: "inherit",
              boxShadow: `0 2px 8px ${confirmCol}40`,
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes neo-dialog-fade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes neo-dialog-pop {
          from { opacity: 0; transform: translateY(-8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );

  // Portal to body so we escape any `overflow: hidden` ancestors.
  return createPortal(overlay, document.body);
}
